// ═══════════════════════════════════════════════════════════════
// EasyRural API client
// Todas las llamadas al backend Node-RED van por aquí.
// ═══════════════════════════════════════════════════════════════

// Base URL del backend. En dev apuntamos al cloud directamente.
// En producción servimos el frontend desde la misma VM, así que
// path relativo basta.
const API_BASE =
  process.env.REACT_APP_API_BASE ||
  (window.location.hostname === "localhost"
    ? "https://easyrural.duckdns.org"
    : "");

async function http(path, opts = {}) {
  const url = `${API_BASE}${path}`;
  const token = localStorage.getItem("easyrural_token");
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(url, { ...opts, headers });
  const text = await res.text();
  let body;
  try {
    body = text ? JSON.parse(text) : null;
  } catch (e) {
    body = text;
  }
  // 401 = token expirado o inválido → forzar re-login
  if (res.status === 401 && token) {
    localStorage.removeItem("easyrural_token");
    localStorage.removeItem("easyrural_user");
    localStorage.removeItem("easyrural_refresh");
    if (typeof window !== "undefined") window.location.reload();
  }
  if (!res.ok) {
    const err = new Error(
      (body && body.error) ||
        (body && body.errors && body.errors.join(", ")) ||
        `HTTP ${res.status}`
    );
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

// ─── Auth endpoints ────────────────────────────────────────────
export const login = (email, password) =>
  http("/api/auth/login", { method: "POST", body: JSON.stringify({ email, password }) });
export const me = () => http("/api/auth/me");
export const listUsers = () => http("/api/users");
export const createUser = (body) =>
  http("/api/users", { method: "POST", body: JSON.stringify(body) });
export const updateUser = (email, body) =>
  http(`/api/users/${encodeURIComponent(email)}`, { method: "PUT", body: JSON.stringify(body) });
export const deleteUser = (email) =>
  http(`/api/users/${encodeURIComponent(email)}`, { method: "DELETE" });

// ─── Health + meta ─────────────────────────────────────────────
export const health = () => http("/api/health");

// ─── Clientes (CRUD) ───────────────────────────────────────────
export const getClients = () => http("/api/clients");
export const getClient = (id) => http(`/api/clients/${encodeURIComponent(id)}`);
export const createClient = (body) =>
  http("/api/clients", { method: "POST", body: JSON.stringify(body) });
export const updateClient = (id, body) =>
  http(`/api/clients/${encodeURIComponent(id)}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
export const deleteClient = (id) =>
  http(`/api/clients/${encodeURIComponent(id)}`, { method: "DELETE" });

// ─── Geoloc para mapa ──────────────────────────────────────────
export const getGeoloc = () => http("/api/geoloc");

// ─── Datadis consumo ───────────────────────────────────────────
export const getConsumption = (id, days = 30) =>
  http(`/api/clients/${encodeURIComponent(id)}/consumption?days=${days}`);
export const getConsumptionSummary = (id) =>
  http(`/api/clients/${encodeURIComponent(id)}/consumption/summary`);
// Datadis on-demand con cache backend por (CUPS, mes).
// start/end en formato YYYY-MM. granularity: hour | day | week | month | year.
// day (obligatorio si granularity=hour) en formato YYYY-MM-DD.
// cups_role: principal | secundario | todos.
export const getConsumptionHistory = (id, opts = {}) => {
  const q = new URLSearchParams();
  if (opts.start)       q.set("start", opts.start);
  if (opts.end)         q.set("end", opts.end);
  if (opts.granularity) q.set("granularity", opts.granularity);
  if (opts.day)         q.set("day", opts.day);
  if (opts.focus_from)  q.set("focus_from", opts.focus_from);
  if (opts.focus_to)    q.set("focus_to", opts.focus_to);
  if (opts.cupsRole)    q.set("cups_role", opts.cupsRole);
  return http(`/api/clients/${encodeURIComponent(id)}/consumption/history?${q.toString()}`);
};
// ─── Ahorro: potencia, solar y tarifas ─────────────────────────
export const getAhorroPotencia = (id) => http(`/api/clients/${encodeURIComponent(id)}/ahorro/potencia`);
export const getAhorroSolar = (id) => http(`/api/clients/${encodeURIComponent(id)}/ahorro/solar`);
export const getAhorroTarifas = (id, params = {}) => {
  const q = new URLSearchParams(Object.entries(params).filter(([, v]) => v !== "" && v !== null && v !== undefined));
  return http(`/api/clients/${encodeURIComponent(id)}/ahorro/tarifas${q.toString() ? "?" + q.toString() : ""}`);
};
export const getContract = (id) =>
  http(`/api/clients/${encodeURIComponent(id)}/contract`);
export const getCosts = (id) =>
  http(`/api/clients/${encodeURIComponent(id)}/costs`);
export const getAlerts = (id) =>
  http(`/api/clients/${encodeURIComponent(id)}/alerts`);
export const getEfficiency = (id) =>
  http(`/api/clients/${encodeURIComponent(id)}/efficiency`);
export const getSustainability = (id) =>
  http(`/api/clients/${encodeURIComponent(id)}/sustainability`);

// ─── AEMET meteo ───────────────────────────────────────────────
export const getMeteo = (id) =>
  http(`/api/clients/${encodeURIComponent(id)}/meteo`);

// ─── ESIOS precios + emisiones ─────────────────────────────────
export const getPrecios = () => http("/api/precios");
export const getPreciosTomorrow = () => http("/api/precios/tomorrow");
export const getEmisiones = () => http("/api/emisiones");

// ─── Sensores Zigbee ───────────────────────────────────────────
// type: 'clima' | 'energia' | 'agua' | 'alarms'
export const getSensors = (id, type = "clima", hours = 24) =>
  http(
    `/api/clients/${encodeURIComponent(id)}/sensors?type=${type}&hours=${hours}`
  );

// ─── Alarmas ───────────────────────────────────────────────────
export const getAlarms = (id, days = 30) =>
  http(`/api/clients/${encodeURIComponent(id)}/alarms?days=${days}`);

// ─── Huella CO2 ────────────────────────────────────────────────
export const getCO2 = (id, days = 30) =>
  http(`/api/clients/${encodeURIComponent(id)}/co2?days=${days}`);
export const getCO2Breakdown = (id) =>
  http(`/api/clients/${encodeURIComponent(id)}/co2/breakdown`);

// ─── Predicción consumo ────────────────────────────────────────
export const getForecast = (id) =>
  http(`/api/clients/${encodeURIComponent(id)}/forecast`);

// ─── Actuadores ────────────────────────────────────────────────
export const postActuator = (id, actuador, orden) =>
  http(`/api/clients/${encodeURIComponent(id)}/actuators`, {
    method: "POST",
    body: JSON.stringify({ actuador, orden }),
  });

// ─── Instalación (Plan Completo) ───────────────────────────────
export const getInstall = (id) => http(`/api/clients/${encodeURIComponent(id)}/install`);
export const putInstall = (id, body) =>
  http(`/api/clients/${encodeURIComponent(id)}/install`, { method: "PUT", body: JSON.stringify(body) });
export const putDevice = (id, device, body) =>
  http(`/api/clients/${encodeURIComponent(id)}/devices/${encodeURIComponent(device)}`, { method: "PUT", body: JSON.stringify(body) });
export const getInstallKit = (id, { ip, unit } = {}) =>
  http(`/api/clients/${encodeURIComponent(id)}/install/kit?ip=${encodeURIComponent(ip || "")}&unit=${encodeURIComponent(unit || 1)}`);

export const postZigbeePermit = (id, segundos) =>
  http(`/api/clients/${encodeURIComponent(id)}/zigbee/permit_join`, { method: "POST", body: JSON.stringify({ segundos }) });

// ─── Diagnóstico de sensores (archivo permanente) ──────────────
// Resumen de todos los equipos de los últimos `dias` días.
export const getDiagnostico = (id, dias = 7) =>
  http(`/api/clients/${encodeURIComponent(id)}/diagnostico?dias=${dias}`);
// Ficha de un equipo. opts: { horas } | { desde, hasta } (AAAA-MM-DD) | { t0, t1 } (ms); campos: lista opcional.
export const getDiagnosticoEquipo = (id, device, opts = {}) => {
  const q = new URLSearchParams();
  ["horas", "desde", "hasta", "t0", "t1"].forEach((k) => { if (opts[k] !== undefined && opts[k] !== null && opts[k] !== "") q.set(k, opts[k]); });
  if (opts.campos && opts.campos.length) q.set("campos", opts.campos.join(","));
  return http(`/api/clients/${encodeURIComponent(id)}/diagnostico/${encodeURIComponent(device)}?${q.toString()}`);
};
// Gráficas globales por tipo de sensor (luz, clima, agua, aparatos). opts: { horas } | { desde, hasta, pdesde } (pdesde = inicio del periodo anterior).
export const getGraficas = (id, opts = {}) => {
  const q = new URLSearchParams();
  ["horas", "desde", "hasta", "pdesde"].forEach((k) => { if (opts[k] !== undefined && opts[k] !== null && opts[k] !== "") q.set(k, opts[k]); });
  return http(`/api/clients/${encodeURIComponent(id)}/graficas?${q.toString()}`);
};
// CSV con todo lo guardado de un equipo (o de todos si device es vacío).
export const getSensorCsv = (id, device, days = 30) =>
  http(`/api/clients/${encodeURIComponent(id)}/sensors/export?days=${days}${device ? `&device=${encodeURIComponent(device)}` : ""}`);

const api = {
  health,
  getClients,
  getClient,
  createClient,
  updateClient,
  deleteClient,
  getGeoloc,
  getConsumption,
  getConsumptionSummary,
  getConsumptionHistory,
  getContract,
  getAhorroPotencia,
  getAhorroSolar,
  getAhorroTarifas,
  getCosts,
  getAlerts,
  getEfficiency,
  getSustainability,
  listUsers,
  createUser,
  updateUser,
  deleteUser,
  login,
  me,
  getMeteo,
  getPrecios,
  getPreciosTomorrow,
  getEmisiones,
  getSensors,
  getAlarms,
  getCO2,
  getCO2Breakdown,
  getForecast,
  postActuator,
  getInstall,
  putInstall,
  putDevice,
  getInstallKit,
  postZigbeePermit,
  getDiagnostico,
  getDiagnosticoEquipo,
  getGraficas,
  getSensorCsv,
};

export default api;
